import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useHistory, useLocation } from "react-router-dom";

import { useDefinedWorker } from "../../../Worker/useDefinedWorker";
import { ChatHeader } from "../../Chat/ChatHeader";
import { useCreateOrGetPlacementCandidate } from "../../PlacementCandidate/api/useCreateOrGetPlacementCandidate";
import { type PlacementWithDetails } from "../../Placements/api/useFetchPaginatedPlacements";
import { getPlacementDetailsPath } from "../../Placements/paths";
import { type PlacementChatMetadata } from "../types";

interface PlacementChatHeaderProps {
  channel: GroupChannel;
  placement?: PlacementWithDetails;
}

export function PlacementChatHeader(props: PlacementChatHeaderProps) {
  const { channel, placement } = props;
  const history = useHistory();
  const location = useLocation();
  const worker = useDefinedWorker();
  const { data: placementCandidate } = useCreateOrGetPlacementCandidate(
    { workerId: worker.userId },
    { enabled: isDefined(worker.userId) }
  );
  const placementChatMetadata = channel.cachedMetaData as PlacementChatMetadata;

  const eventContext = {
    workplaceId: placement?.workplace.id,
    managingWorkplaceId: placementChatMetadata.managingFacilityId,
    workerId: worker.userId,
    placementId: placement?.id,
    userId: worker.userId,
    userType: "AGENT",
    userEmail: worker.email,
    source: "chatHeader",
  };

  function handleGoBack() {
    if (isDefined(location.key)) {
      history.goBack();
    } else {
      history.push(DeprecatedGlobalAppV1Paths.OPEN_SHIFTS);
    }
  }

  function handleViewListing() {
    if (!isDefined(placement) || !isDefined(placementCandidate)) {
      return;
    }
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_CANDIDATE_CHAT_ACTION, {
      ...eventContext,
      action: "viewListingButtonClick",
    });
    history.push(getPlacementDetailsPath(placement.id, placementCandidate.id));
  }

  const voiceNoteUrl = placement?.applications?.[0]?.attributes.workerVoiceNoteUrl;

  return (
    <ChatHeader
      title={placementChatMetadata?.facilityName ?? ""}
      subtitle={placementChatMetadata?.placementTitle}
      onBack={() => {
        handleGoBack();
      }}
      onTitleClick={() => {
        handleViewListing();
      }}
    >
      {voiceNoteUrl && (
        <Stack alignItems="center" spacing={1}>
          <Text variant="body2">Your voice note</Text>
          <audio controls src={voiceNoteUrl}>
            <track kind="captions" />
          </audio>
        </Stack>
      )}
    </ChatHeader>
  );
}
